// src/utils/date.ts

// Get the user's timezone from their profile, falling back to browser timezone.
export const getUserTimezone = (): string => {
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      if (userData.timezone) return userData.timezone;
    }
  } catch { /* ignore parse errors */ }
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Format a date to French locale in the user's timezone
export const formatDateToFrench = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: getUserTimezone()
  };
  return new Intl.DateTimeFormat('fr-FR', options).format(dateObj);
};

// Format time in 24-hour format
export const formatTime = (time: string): string => {
  const [hours, minutes] = time.split(':');
  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
};

// Get today's date in Ivory Coast timezone
export const getIvoryCoastToday = (): Date => {
  const now = new Date();
  const utcDate = new Date(now.toUTCString());
  return utcDate;
};

// Check if a date is in the past (Ivory Coast timezone)
export const isDateInPast = (date: Date): boolean => {
  const today = getIvoryCoastToday();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

// Format date to YYYY-MM-DD using local time
export const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// yapadra-backend's `nextAvailable` field is a computed "YYYY-MM-DD" string,
// but it's also the sentinel literal "Non disponible" whenever the therapist
// has no available day in the next 14 days — not every value is a real date.
// Feeding that sentinel into `new Date(...)` produces an Invalid Date object,
// which renders as the literal text "Invalid Date" in the UI, so this must be
// checked before formatting rather than assumed to always be a parseable date.
const NEXT_AVAILABLE_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const formatNextAvailable = (nextAvailable: string): string => {
  if (!NEXT_AVAILABLE_DATE_RE.test(nextAvailable)) {
    return nextAvailable;
  }
  const parsed = new Date(`${nextAvailable}T00:00:00`);
  if (isNaN(parsed.getTime())) {
    return nextAvailable;
  }
  return parsed.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};